import { useEffect, useMemo, useState } from 'react';

import config from '@/config';
import useQuery from '@/hooks/useQuery';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import ArtistVerifiedIcon from '@/shared-components/icons/components/user-profile/ArtistVerifiedIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import InspiringNavigation from '../components/InspiringNavigation';

import SearchFollowing from './search-following';
import SearchInspiring from './search-inspiring';

import './index.css';

function DisplayNameUsername({ fetchData }) {
  const userName = typeof fetchData.user_name === 'string' ? fetchData.user_name.trim() : '';
  const userUsername = typeof fetchData.user_username === 'string' ? fetchData.user_username.trim() : '';
  const shouldShowDisplayName =
    (fetchData.user_type === 'gallery' || fetchData.user_type === 'artist') &&
    Boolean(userName) &&
    Boolean(userUsername);

  if (shouldShowDisplayName) {
    return (
      <Text
        className="search-inspiring__user_name dots-on-overflow"
        paragraph>
        {userName}
        {fetchData.user_type === 'artist' && fetchData.user_flags?.suarteroad_completed ? (
          <ArtistVerifiedIcon className="search-inspiring__artist-verified-icon" />
        ) : (
          <></>
        )}
      </Text>
    );
  }

  return (
    <Text
      className="search-inspiring__user_username dots-on-overflow"
      paragraph>
      {userUsername}
    </Text>
  );
}

export default function Inspiring({ open, close, fetchData, numberOfInspiring }) {
  const t = useTranslations('common.profile.inspiring');
  const [currentPage, setCurrentPage] = useState('inspiring');
  const [followingCount, setFollowingCount] = useState(0);
  const { cacheHandler } = useStateHandler();

  useEffect(() => {
    if (open) {
      setCurrentPage('inspiring');
    }
  // Reset the active tab whenever the popup is reopened
  }, [open]);

  // Keep memoized references so the query hook does not refetch unnecessarily
  const stableQuery = useMemo(() => ({ username: '' }), []);
  const stableOptions = useMemo(
    () => ({
      injectToken: true,
      invalidateWhen: [`FOLLOW_ACTION_ON_${fetchData._id}`],
    }),
    [fetchData._id]
  );

  const { queryData } = useQuery(
    `${fetchData._id}_following`,
    `${config.apis.api.url}/user/followees/${fetchData._id}`,
    stableQuery,
    stableOptions
  );

  const cacheKey = useMemo(
    () => `${fetchData._id}_following.?`,
    [fetchData._id]
  );

  // Keep the tab badges synchronized with either fresh data or the cached snapshot every time the popup opens.
  useEffect(() => {
    if (!open) return;

    const cachedTotal = cacheHandler.getCacheValue(cacheKey)?.data?.data?.totalDocs;
    const next = queryData?.data?.totalDocs ?? cachedTotal ?? 0;

    setFollowingCount(next);
  }, [cacheHandler, cacheKey, open, queryData, setFollowingCount]);

  return (
    <>
      <GenericPopup open={open} className="search-inspiring__popup" opacity>
        <div className="search-inspiring__popup-header">
          <button
            type="button"
            className="search-inspiring__popup-action search-inspiring__popup-back-button"
            onClick={close}
            aria-label={t('aria.close_popup')}>
            <BackArrowIcon />
          </button>

          <div className="search-inspiring__popup-header-title">
            <DisplayNameUsername fetchData={fetchData} />
          </div>

          <XIcon
            className="search-inspiring__popup-close-button"
            onClick={close}
            aria-label={t('aria.close_popup')}
          />
        </div>

        <InspiringNavigation
          userProfileId={fetchData?._id}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          inspiringCount={numberOfInspiring}
          followingCount={followingCount}
        />

        <div className="search-inspiring__content remove-scrollbar">
          {currentPage === 'inspiring' ? (
            <SearchInspiring userId={fetchData._id} />
          ) : (
            <SearchFollowing
              userId={fetchData._id}
            />
          )}
        </div>
      </GenericPopup>
    </>
  );
}
